import * as React from "react";

import { Radio, Select, Upload, Form } from "antd";
import { Plus } from "lucide-react";
import { Icon } from "src/components/icons/Icon";

import DatasetAPI from "./DatasetAPI";
import ReportSettingAPI from "src/admin/Organization/ReportSetting/ReportSettingAPI";
import LocationAPI from "src/admin/Organization/Location/LocationAPI";

import { Validation } from "src/common/utils";
import AuthorizationWrapper from "src/common/AuthorizationWrapper";
import pickListContext from "src/context/pickListContext";
import WhiteLabelingForm from "src/components/forms/WhiteLabelingForm";
import { Button } from "src/components/buttons";
import { AdminPermissions } from "src/common/Permissions";
import { Page, Spinner } from "src/components/common";
import moment from "moment";
import { toast } from "react-toastify";

interface IProps {
	match: any;
	history: any;
}

interface IState {
	dataset: DatasetDTO;
	reportSetting: ReportSettingDTO;
	orgLocations: LocationDTO[];
	datasetLocations: LocationDTO[];
	isLoading: boolean;
	override: boolean;

	uploadLoading: boolean;
	hideUpload: boolean;
	fileList: File[];
	fileUrl: string;
}

class OrganizationDatasetsReportSettings extends React.Component<
	IProps,
	IState
> {
	public static contextType = pickListContext;
	public constructor(props: any) {
		super(props);
		this.state = {
			dataset: {},
			reportSetting: {
				primaryColor: "#000000",
				secondaryColor: "#000000",
			},
			orgLocations: [],
			datasetLocations: [],
			isLoading: true,
			override: false,
			uploadLoading: false,
			hideUpload: false,
			fileList: [],
			fileUrl: "",
		};
	}

	public componentDidMount() {
		const organizationId = this.props.match.params.organizationId;
		const datasetId = this.props.match.params.datasetId;

		const p1 = DatasetAPI.getOrganizationDataset(organizationId, datasetId)
			.then((returnedDataset) => this.setState({ dataset: returnedDataset }))
			.then(() =>
				this.state.dataset.reportSettingId === 0 ||
				this.state.dataset.reportSettingId === undefined
					? this.getReportSetting(organizationId)
					: this.getOrganizationDatasetReportSetting(organizationId, datasetId)
			)
			.then((reportSetting) =>
				this.setState({
					reportSetting,
					fileUrl: reportSetting.logoUrl ? reportSetting.logoUrl! : "",
				})
			);
		const p2 = LocationAPI.getDatasetLocations(organizationId, datasetId).then(
			(response) => this.setState({ datasetLocations: response })
		);
		const p3 = LocationAPI.getOrganizationLocations(organizationId).then(
			(response) => this.setState({ orgLocations: response })
		);
		Promise.all([p1, p2, p3]).then(() => this.setState({ isLoading: false }));
	}

	public render() {
		const { reportSetting, override, isLoading, fileUrl } = this.state;
		const uploadButton = this.setUploadButton();
		const datasetReportSettingImage = this.displayDatasetReportSettingImage();

		const validDatasetReportSetting = {
			datasetShowLogo: Validation.validateIsActive(reportSetting.showLogo!),
			datasetShowContactInfo: Validation.validateIsActive(
				reportSetting.showContactInfo!
			),
			datasetWhiteLabel: Validation.validateIsActive(reportSetting.whiteLabel!),
		};

		const locationOptions = this.context.pickListItems
			.filter(
				(p: PickListItemResponseDTO) =>
					p.pickListTypeCode === "ReportSettingLocation"
			)
			.swapItems(0, 1);
		const useReportSettingOption = locationOptions.find(
			(p: PickListItemResponseDTO) => p.code === "UseReportSetting"
		)!;

		return this.state.isLoading !== false ? (
			<Spinner />
		) : (
			<Page title="Dataset Branding">
				<div className="container-fluid">
					<div className="row">
						<div className="col-2 form-group">
							<label>Override organization branding</label>
							<Form.Item>
								<AuthorizationWrapper
									type="disable"
									permissions={[AdminPermissions.EditDatasetReportSetting]}
								>
									<Radio.Group
										name="override"
										onChange={this.onOverrideChange}
										value={override}
									>
										{[
											{ label: "Yes", value: true },
											{ label: "No", value: false },
										].map((item, index) => {
											return (
												<Radio key={index} value={item.value}>
													{item.label}
												</Radio>
											);
										})}
									</Radio.Group>
								</AuthorizationWrapper>
							</Form.Item>
						</div>
					</div>
					<div className="row">
						<div className="col-2 form-group">
							<label>Show logo</label>
							<Form.Item
								hasFeedback={true}
								validateStatus={
									validDatasetReportSetting.datasetShowLogo.validationStatus
								}
								help={validDatasetReportSetting.datasetShowLogo.message}
							>
								<AuthorizationWrapper
									type="disable"
									permissions={[AdminPermissions.EditDatasetReportSetting]}
								>
									<Radio.Group
										name="showLogo"
										onChange={this.onReportSettingChange}
										value={reportSetting.showLogo}
										disabled={!override}
									>
										{[
											{ label: "Yes", value: true },
											{ label: "No", value: false },
										].map((item, index) => {
											return (
												<Radio key={index} value={item.value}>
													{item.label}
												</Radio>
											);
										})}
									</Radio.Group>
								</AuthorizationWrapper>
							</Form.Item>
						</div>
						<div className="col-5 form-group">
							<label>Logo</label>
							<AuthorizationWrapper
								type="disable"
								permissions={[AdminPermissions.EditDatasetReportSetting]}
							>
								{
									<Upload
										name="organizationReportSettingLogo"
										listType="picture-card"
										accept="image/*"
										showUploadList={false}
										disabled={!override || isLoading}
										customRequest={this.handleCustomUploadRequest} // trick upload that an action ('url to post on') was provided
									>
										{fileUrl ? datasetReportSettingImage : uploadButton}
									</Upload>
								}
							</AuthorizationWrapper>
						</div>
					</div>
					<div className="row">
						<div className="col form-group">
							<label>White labeling</label>
							<Form.Item
								hasFeedback={true}
								validateStatus={
									validDatasetReportSetting.datasetWhiteLabel.validationStatus
								}
								help={validDatasetReportSetting.datasetWhiteLabel.message}
							>
								<AuthorizationWrapper
									type="disable"
									permissions={[AdminPermissions.EditDatasetReportSetting]}
								>
									<Radio.Group
										name="whiteLabel"
										onChange={this.onReportSettingChange}
										value={reportSetting.whiteLabel}
										disabled={!override}
									>
										{[
											{ label: "Yes", value: true },
											{ label: "No", value: false },
										].map((item, index) => {
											return (
												<Radio key={index} value={item.value}>
													{item.label}
												</Radio>
											);
										})}
									</Radio.Group>
								</AuthorizationWrapper>
							</Form.Item>
						</div>
					</div>
					{this.state.reportSetting.whiteLabel && (
						<WhiteLabelingForm
							reportSetting={reportSetting}
							onReportSettingChange={this.onReportSettingChange}
						/>
					)}
					<div className="row">
						<div className="col">
							<label className="font-weight-bold w-100">
								Above settings will override the branding at organizational
								level.
							</label>
							<AuthorizationWrapper
								type="hide"
								permissions={[AdminPermissions.EditDatasetReportSetting]}
							>
								<Button
									className="btn-primary"
									disabled={!this.isValid(validDatasetReportSetting)}
									onClick={this.onSubmit}
								>
									Save
								</Button>
							</AuthorizationWrapper>
						</div>
					</div>
				</div>
			</Page>
		);
	}

	private getReportSetting = (organizationId: number) => {
		return ReportSettingAPI.getReportSetting({ organizationId }).then(
			(reportSetting) => reportSetting
		);
	};

	private getOrganizationDatasetReportSetting = (
		organizationId: number,
		datasetId: number
	) => {
		return DatasetAPI.getOrganizationDatasetReportSettings(
			organizationId,
			datasetId
		).then((reportSetting) => {
			this.setState({ override: true });
			return reportSetting;
		});
	};

	private displayDatasetReportSettingImage = () => {
		const { fileUrl } = this.state;
		return (
			<img
				src={fileUrl}
				alt="dataset reportsetting logo"
				className="org-logo"
			/>
		);
	};

	private setUploadButton = () => {
		return (
			<AuthorizationWrapper
				type="hide"
				permissions={[AdminPermissions.EditDatasetReportSetting]}
			>
				<div>
					<Icon icon={Plus} />
					<div className="ant-upload-text">Upload</div>
				</div>
			</AuthorizationWrapper>
		);
	};

	private handleCustomUploadRequest = (options: any) => {
		const { reportSetting } = this.state;
		const { onSuccess, onError, file } = options;
		if (onError === true) {
			onError("Something went wrong");
			return;
		}

		setTimeout(() => {
			onSuccess(this.setDatasetReportSettingLogo(reportSetting, file));
		}, 0);
	};

	private setDatasetReportSettingLogo = (
		reportSetting: ReportSettingDTO,
		logoFile: File
	) => {
		const reportSettingWithLogoUrl = {
			...reportSetting,
			logoUrl: URL.createObjectURL(logoFile),
		};
		this.setState({
			fileList: [logoFile],
			fileUrl: URL.createObjectURL(logoFile),
			reportSetting: reportSettingWithLogoUrl,
		});
	};

	private isValid = (validDatasetReportSetting: any) => {
		if (!this.state.override) {
			return true;
		}
		// const useReportSetting = this.context.pickListItems.find((p: PickListItemResponseDTO) => p.code === 'UseReportSetting');
		let validateValues: string[] = [];
		validateValues = Object.keys(validDatasetReportSetting).map(
			(key) => validDatasetReportSetting[key].validationStatus
		);
		return Validation.isValid(validateValues);
	};

	private onOverrideChange = (evt: any) => {
		const useTestScreenOption = this.context.pickListItems.find(
			(p: PickListItemResponseDTO) => p.code === "UseTestScreen"
		)!;
		// if uncheck override then it needs to make sure dataset uses organization's branding
		if (evt.target.value === false) {
			this.reloadOrganizationReportSettings();
			const dataset = { ...this.state.dataset };
			dataset.reportSettingId = 0;
			this.setState({ dataset });
		} else {
			const reportSetting = { ...this.state.reportSetting };
			reportSetting.id = this.state.dataset.reportSettingId
				? this.state.dataset.reportSettingId
				: undefined;
			reportSetting.showContactInfo = true;
			reportSetting.showLogo = true;
			reportSetting.whiteLabel = true;
			this.setState({ reportSetting });
		}
		this.setState({ override: evt.target.value });
	};

	private reloadOrganizationReportSettings = () => {
		const organizationId = this.props.match.params.organizationId;
		this.setState({ isLoading: true });
		ReportSettingAPI.getReportSetting({ organizationId })
			.then((reportSetting) =>
				this.setState({ reportSetting, fileUrl: reportSetting.logoUrl! })
			)
			.then(() => this.setState({ isLoading: false }));
	};

	private onReportSettingChange = (e: any) => {
		let value = e.target.value!;
		if (value === "true") {
			value = true;
		}
		if (value === "false") {
			value = false;
		}
		let name = e.target.name;
		if (e.target.type === "radio") {
			value = value;
		} else if (e.target.type === "checkbox") {
			if (e.target.data !== undefined) {
				// If there's data then it's a checkbox group
				if (e.target.data.singleSelect) {
					value = value;
				} else {
					// Don't split if single value
					if (value === "") {
						value = [];
					} else {
						value = value.split(",");
					}
				}
				name = e.target.data.name;
			} else {
				value = e.target.checked;
			}
		}
		const reportSetting = {
			...this.state.reportSetting,
			[name]: value,
		};
		this.setState({ reportSetting });
	};

	private getDatasetReportSettingLogo = () => {
		return this.state.fileList[0];
	};

	private upsertDatasetReportSettingsWithoutLogo = (
		organizationId: number,
		datasetId: number,
		reportSetting: ReportSettingDTO
	) => {
		return DatasetAPI.upsertOrganizationDatasetReportSetting(
			organizationId,
			datasetId,
			reportSetting
		).then((savedReportSetting) => {
			this.setState({ isLoading: false, reportSetting: savedReportSetting });
			toast.success("Dataset report setting updated!");
		});
	};

	private upsertDatasetWithoutOverride = (
		organizationId: number,
		dataset: DatasetDTO
	) => {
		dataset.modifiedOn = moment().toISOString();
		return DatasetAPI.upsertOrganizationDataset(organizationId, dataset).then(
			(savedReportSetting) => {
				this.setState({ isLoading: false, reportSetting: savedReportSetting });
				toast.success("Dataset report setting updated!");
			}
		);
	};

	private upsertDatasetReportSettingsWithLogo = (
		organizationId: number,
		datasetId: number,
		reportSetting: ReportSettingDTO
	) => {
		const datasetReportSettingLogo = this.getDatasetReportSettingLogo();
		ReportSettingAPI.upsertReportSettingLogo(datasetReportSettingLogo).then(
			(logoUrl) => {
				reportSetting.logoUrl = logoUrl;
				this.setState({ reportSetting });
				DatasetAPI.upsertOrganizationDatasetReportSetting(
					organizationId,
					datasetId,
					reportSetting
				).then((savedReportSetting) => {
					this.setState({
						isLoading: false,
						reportSetting: savedReportSetting,
					});
					toast.success("Dataset report setting updated!");
				});
			}
		);
	};

	private onSubmit = () => {
		const organizationId = +this.props.match.params.organizationId;
		const datasetId = +this.props.match.params.datasetId;
		const { override, dataset, reportSetting, fileList } = this.state;
		if (!override) {
			return this.upsertDatasetWithoutOverride(organizationId, dataset);
		} else {
			if (
				reportSetting.logoUrl === undefined ||
				reportSetting.logoUrl === "" ||
				!fileList[0]
			) {
				return this.upsertDatasetReportSettingsWithoutLogo(
					organizationId,
					datasetId,
					reportSetting
				);
			}

			return this.upsertDatasetReportSettingsWithLogo(
				organizationId,
				datasetId,
				reportSetting
			);
		}
	};
}
export default OrganizationDatasetsReportSettings;
